// /////////////////////////////////////////////////////////////
// [ファイル] ClientContactsSection.tsx
// [概要] SFA案件詳細で、取引先(法人)に紐づく「先方担当者」を一覧表示するセクション。
//        先方担当者の実体は CRM 個人顧客 (employer_id = 取引先の法人ID) で、
//        テーブル / カード表示の切替、追加・編集・削除(勤務先の紐付け解除)に対応する。
//        削除は顧客レコードを消さず employer_id を NULL にして一覧から外す安全な振る舞い。
// /////////////////////////////////////////////////////////////
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Fab,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Link,
  Stack,
  Collapse,
  Button,
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import * as Icons from '../constants/icons';
import LoadingView from './LoadingView';
import ContactFormDialog from './ContactFormDialog';
import { useAuth } from '../firebase/AuthProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { useNotification } from '../providers/NotificationProvider';
import { STANDARD_FAB_CTA_SX } from '../constants/buttonStyles';
import { ep } from '../api/endpoints';

const GET_CUSTOMERS_URL = ep('get_crm_customers');
const DETACH_URL = ep('detach_client_contact');
const FORMER_URL = ep('get_crm_former_contacts');

// 勤務先履歴の過去行 (この法人に在籍していた元担当者)
interface FormerContact {
  id: number;
  customer_id: string;
  last_name?: string | null;
  first_name?: string | null;
  last_name_kana?: string | null;
  first_name_kana?: string | null;
  department?: string | null;
  occupation?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  note?: string | null;
  current_employer_id?: string | null;
  current_employer_name?: string | null;
}

const fmtDate = (d?: string | null) => (d ? d.replace(/-/g, '/') : '');

interface ClientContactsSectionProps {
  clientId: string;        // 取引先(法人)の顧客ID = employer_id
  clientName?: string;
}

const fullName = (c: any) => `${c.last_name || ''} ${c.first_name || ''}`.trim();
const fullKana = (c: any) => `${c.last_name_kana || ''} ${c.first_name_kana || ''}`.trim();

const ClientContactsSection: React.FC<ClientContactsSectionProps> = ({ clientId, clientName }) => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const notify = useNotification();

  const [contacts, setContacts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<any | null>(null);
  const [former, setFormer] = useState<FormerContact[]>([]);
  const [formerOpen, setFormerOpen] = useState(false);
  const navigate = useNavigate();

  // 元担当者 (勤務先履歴で この法人を勤務先としていた過去行)
  const fetchFormer = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await axios.post(FORMER_URL, { employerId: clientId });
      if (res.data.status === 'success') setFormer(res.data.rows || []);
    } catch (err) {
      console.error('Error fetching former contacts:', err);
    }
  }, [clientId]);

  // 取引先(法人)を勤務先とする個人顧客=先方担当者を取得
  const fetchContacts = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await axios.post(GET_CUSTOMERS_URL, {
        filters: { employer_id: clientId, customer_type: 'individual' },
        page: 0,
        pageSize: 100,
      });
      if (res.data.status === 'success') {
        setContacts(res.data.rowsResult || []);
      }
    } catch (err) {
      console.error('Error fetching client contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchContacts();
    fetchFormer();
  }, [fetchContacts, fetchFormer]);

  const handleAdd = () => {
    setEditingContact(null);
    setDialogOpen(true);
  };

  const handleEdit = (contact: any) => {
    setEditingContact(contact);
    setDialogOpen(true);
  };

  const handleDelete = async (contact: any) => {
    const ok = await confirm({
      title: '先方担当者を一覧から外す',
      message: `「${fullName(contact) || '(氏名未設定)'}」をこの取引先の先方担当者一覧から外します。\n顧客情報そのものは CRM に残ります。よろしいですか？`,
      destructive: true,
      confirmText: '一覧から外す',
      cancelText: 'キャンセル',
    });
    if (!ok) return;
    try {
      const res = await axios.post(DETACH_URL, {
        customerId: contact.id,
        operator_id: user?.email,
      });
      if (res.data.status === 'success') {
        notify.success('先方担当者を一覧から外しました');
        fetchContacts();
        fetchFormer();
      } else {
        notify.error(res.data.message || '処理に失敗しました');
      }
    } catch (err) {
      console.error('Error detaching contact:', err);
      notify.error('処理に失敗しました');
    }
  };

  const emailLink = (email: string) =>
    email ? (
      <Link
        href={`https://mail.google.com/mail/?view=cm&fs=1&to=${email}`}
        target="_blank"
        rel="noopener"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
      >
        <Icons.EmailOutlined sx={{ fontSize: 15, color: 'text.disabled' }} />
        {email}
      </Link>
    ) : (
      <Typography component="span" variant="body2" color="text.disabled">-</Typography>
    );

  const phoneText = (phone: string) =>
    phone ? (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <Icons.PhoneOutlined sx={{ fontSize: 15, color: 'text.disabled' }} />
        {phone}
      </Box>
    ) : (
      <Typography component="span" variant="body2" color="text.disabled">-</Typography>
    );

  return (
    <Box
      sx={{
        bgcolor: 'white',
        borderRadius: 2,
        p: 2.5,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Box
          sx={{
            bgcolor: 'primary.light',
            color: 'white',
            width: 25,
            height: 25,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icons.People fontSize="small" />
        </Box>
        <Typography variant="subtitle1" color="text.secondary">先方担当者</Typography>
        <Chip label={`${contacts.length}名`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            onChange={(_, v) => v && setViewMode(v)}
            sx={{ '& .MuiToggleButton-root': { px: 1, py: 0.25 } }}
          >
            <ToggleButton value="table" aria-label="テーブル表示">
              <Icons.TableChart sx={{ fontSize: 16, mr: 0.5 }} /> テーブル
            </ToggleButton>
            <ToggleButton value="card" aria-label="カード表示">
              <Icons.ViewModule sx={{ fontSize: 16, mr: 0.5 }} /> カード
            </ToggleButton>
          </ToggleButtonGroup>
          <Fab variant="extended" onClick={handleAdd} sx={STANDARD_FAB_CTA_SX}>
            <Icons.PersonAdd />
            担当者を追加
          </Fab>
        </Box>
      </Box>

      {/* 本文 */}
      {loading ? (
        <LoadingView message="読み込み中..." height="120px" size={36} />
      ) : contacts.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          先方担当者が登録されていません。「担当者を追加」から登録できます。
        </Typography>
      ) : viewMode === 'table' ? (
        // ============================
        // テーブル表示
        // ============================
        <TableContainer>
          {/*
            狭い領域に配置されると見出し・氏名・メール・電話・操作アイコンが
            文字単位で折り返されて崩れるため、セルは折り返し禁止とし、
            幅が足りない場合は TableContainer 側で横スクロールさせる。
          */}
          <Table
            size="small"
            sx={{
              '& .MuiTableCell-root': { whiteSpace: 'nowrap' },
              '& .MuiTableCell-head': { fontWeight: 600 },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>担当者</TableCell>
                <TableCell>部署</TableCell>
                <TableCell>役職</TableCell>
                <TableCell>メールアドレス</TableCell>
                <TableCell>携帯番号</TableCell>
                <TableCell align="right" sx={{ width: '1%' }}>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                          {fullName(c) || '(氏名未設定)'}
                        </Typography>
                        {fullKana(c) && (
                          <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.2 }}>
                            {fullKana(c)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{c.department || <Typography component="span" variant="body2" color="text.disabled">-</Typography>}</TableCell>
                  <TableCell>{c.occupation || <Typography component="span" variant="body2" color="text.disabled">-</Typography>}</TableCell>
                  <TableCell>{emailLink(c.business_email)}</TableCell>
                  <TableCell>{phoneText(c.business_mobile)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(c)} title="編集">
                      <Icons.Edit sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(c)} title="一覧から外す">
                      <Icons.PersonRemove sx={{ fontSize: 18 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        // ============================
        // カード表示
        // ============================
        <Grid container spacing={1.5}>
          {contacts.map((c) => (
            <Grid key={c.id} size={{ xs: 12, sm: 6 }}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                        {fullName(c) || '(氏名未設定)'}
                      </Typography>
                      {fullKana(c) && (
                        <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.2 }}>
                          {fullKana(c)}
                        </Typography>
                      )}
                    </Box>
                    <IconButton size="small" onClick={() => handleEdit(c)} title="編集">
                      <Icons.Edit sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(c)} title="一覧から外す">
                      <Icons.PersonRemove sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                  <Stack spacing={0.5}>
                    {(c.department || c.occupation) && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {c.department && <Chip label={c.department} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />}
                        {c.occupation && <Chip label={c.occupation} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />}
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary">{emailLink(c.business_email)}</Typography>
                    <Typography variant="caption" color="text.secondary">{phoneText(c.business_mobile)}</Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* 元担当者 (勤務先履歴の過去行)。転職・異動で一覧から外れた人を折りたたみで残す */}
      {former.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            onClick={() => setFormerOpen((v) => !v)}
            startIcon={formerOpen ? <Icons.ExpandLess /> : <Icons.ExpandMore />}
            sx={{ textTransform: 'none', color: 'text.secondary', px: 0.5 }}
          >
            元担当者 {former.length}名
          </Button>
          <Collapse in={formerOpen}>
            <TableContainer>
              <Table size="small" sx={{ '& .MuiTableCell-root': { whiteSpace: 'nowrap' }, '& .MuiTableCell-head': { fontWeight: 600 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>氏名</TableCell>
                    <TableCell>当時の部署 / 役職</TableCell>
                    <TableCell>在籍期間</TableCell>
                    <TableCell>現在の勤務先</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {former.map((f) => (
                    <TableRow key={f.id} hover>
                      <TableCell>
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={() => navigate(`/crm/customers/${f.customer_id}`)}
                          sx={{ textAlign: 'left' }}
                        >
                          {fullName(f) || '(氏名未設定)'}
                        </Link>
                        {fullKana(f) && (
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', lineHeight: 1.2 }}>
                            {fullKana(f)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{[f.department, f.occupation].filter(Boolean).join(' / ') || <Typography component="span" variant="body2" color="text.disabled">-</Typography>}</TableCell>
                      <TableCell>{`${fmtDate(f.start_date) || '不明'} 〜 ${fmtDate(f.end_date) || '不明'}`}</TableCell>
                      <TableCell>
                        {f.current_employer_id && f.current_employer_name ? (
                          <Link component="button" type="button" variant="body2" onClick={() => navigate(`/crm/customers/${f.current_employer_id}`)} sx={{ textAlign: 'left' }}>
                            {f.current_employer_name}
                          </Link>
                        ) : (
                          <Typography component="span" variant="body2" color="text.disabled">-</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Box>
      )}

      {/* 追加・編集ダイアログ */}
      {dialogOpen && (
        <ContactFormDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          employerId={clientId}
          employerName={clientName}
          contact={editingContact}
          onSaved={fetchContacts}
        />
      )}
    </Box>
  );
};

export default ClientContactsSection;
